import {
  AUTO_MOD_RULE_META,
  applyAutoModPreset,
  cloneAutoModPolicy,
  normaliseAutoModPolicy,
  parseListInput,
  serialiseListInput,
  toggleSelection,
  type AutoModPolicy,
  type AutoModPolicyResponse,
  type AutoModRuleId
} from "./automod.js";
import {
  formatAbsoluteTime,
  formatCountdown,
  formatRelativeTime,
  formatThreatLabel,
  type ThreatLevel
} from "./formatters.js";
import {
  resolveGuildSelection,
  type DashboardGuildSummary
} from "./guildSelection.js";
import {
  RULE_EVENT_LABELS,
  RULE_SEVERITY_LABELS,
  RULE_THREAT_LABELS,
  createBlankRuleDraft,
  describeRuleAction,
  ruleDraftFromDefinition,
  sortRulesByRecency,
  validateRuleDraft,
  type RuleActionKind,
  type RuleDefinition,
  type RuleDraft,
  type RuleEventName,
  type RuleParseResponse,
  type RuleSeverity,
  type RuleThreatLevel,
  type RulesListResponse
} from "./rules.js";

interface HealthResponse {
  ok: boolean;
  reportedAt: string;
  guildId: string | null;
  bot?: {
    reachable: boolean;
    latencyMs?: number;
  };
}

interface SessionResponse {
  userId: string;
  username: string;
  issuedAt: number;
  expiresAt: number;
  defaultGuildId: string | null;
}

interface GuildsResponse {
  guilds: DashboardGuildSummary[];
}

interface ThreatResponse {
  guildId: string;
  threatLevel: ThreatLevel;
  lockdownMode: string;
  lastSignalAt: string | null;
  source: string | null;
  reason: string | null;
  cooldowns: {
    deescalationMsRemaining: number;
  };
}

interface ModuleState {
  id: string;
  label: string;
  description: string;
  tier: "free" | "premium";
  enabled: boolean;
  available: boolean;
}

interface ModulesResponse {
  modules: ModuleState[];
}

interface IncidentRecord {
  id: string;
  guildId: string;
  kind: string;
  severity: "low" | "medium" | "high" | "critical";
  summary: string;
  details: string[];
  actorId?: string;
  createdAt: string;
}

interface IncidentsResponse {
  items: IncidentRecord[];
  pageInfo: {
    limit: number;
    cursor: string;
    nextCursor: string | null;
  };
}

interface RestoreApproval {
  moderatorId: string;
  approvedAt: string;
}

interface RestoreRecord {
  id: string;
  guildId: string;
  actorId: string;
  source: string;
  reason: string;
  approvals: RestoreApproval[];
  requiredApprovals: number;
  strippedRoleIds: string[];
  restoredRoleIds: string[];
  status: "pending_approval" | "approved" | "restored";
  createdAt: string;
  updatedAt: string;
}

interface RestoreResponse {
  restores: RestoreRecord[];
}

interface SessionPlaceholder {
  userId: string | null;
  username: string;
  issuedAt: number;
  expiresAt: number;
  defaultGuildId: string | null;
}

type SessionState =
  | ({ status: "loading" } & SessionPlaceholder)
  | ({ status: "unauthenticated" } & SessionPlaceholder)
  | ({ status: "ready" } & SessionResponse);

type DashboardPage =
  | "onboarding"
  | "overview"
  | "defense"
  | "incidents"
  | "restores"
  | "automod"
  | "rules";
type DashboardView = DashboardPage;
type NoticeTone = "info" | "success" | "error";
type AutoModListField = "keywords" | "regexPatterns" | "allowList";

interface AppState {
  health: HealthResponse | null;
  session: SessionState;
  guildId: string | null;
  availableGuilds: DashboardGuildSummary[];
  threat: ThreatResponse | null;
  modules: ModuleState[];
  incidents: IncidentRecord[];
  incidentCursor: string | null;
  restores: RestoreRecord[];
  autoMod: AutoModPolicyResponse | null;
  autoModDraft: AutoModPolicy | null;
  rules: RulesListResponse | null;
  ruleDraft: RuleDraft | null;
  ruleParseStatus: { state: "idle" | "checking" | "valid" | "invalid"; message: string };
  notice: { tone: NoticeTone; text: string } | null;
  dashboardLoading: boolean;
  busyKey: string | null;
  threatReasonDraft: string;
  activeView: DashboardView;
  activePage: DashboardPage;
  onboardingComplete: boolean;
  selectedIncidentId: string | null;
}

const AUTO_MOD_RULE_ORDER: AutoModRuleId[] = [
  "spamContent",
  "mentionSpam",
  "securityPhrases",
  "inviteLinks",
  "verificationLures",
  "profileNames"
];
const DASHBOARD_PAGE_ORDER: DashboardPage[] = [
  "onboarding",
  "overview",
  "defense",
  "incidents",
  "restores",
  "automod",
  "rules"
];
const DASHBOARD_VIEW_ORDER: DashboardView[] = ["overview", "defense", "automod"];

const root = getRoot();

const state: AppState = {
  health: null,
  session: buildSessionPlaceholder("loading"),
  guildId: null,
  availableGuilds: [],
  threat: null,
  modules: [],
  incidents: [],
  incidentCursor: null,
  restores: [],
  autoMod: null,
  autoModDraft: null,
  rules: null,
  ruleDraft: null,
  ruleParseStatus: { state: "idle", message: "" },
  notice: null,
  dashboardLoading: true,
  busyKey: null,
  threatReasonDraft: "",
  activeView: "overview",
  activePage: "overview",
  onboardingComplete: false,
  selectedIncidentId: null
};

void bootstrap();

function buildSessionPlaceholder(status: "loading" | "unauthenticated"): SessionState {
  return {
    status,
    userId: null,
    username: "",
    issuedAt: 0,
    expiresAt: 0,
    defaultGuildId: null
  };
}

async function bootstrap() {
  const url = new URL(window.location.href);
  state.activePage = resolveDashboardPage(url.hash.slice(1), readStoredDashboardPage());
  state.activeView = state.activePage;
  const error = url.searchParams.get("error");
  if (error) {
    state.notice = {
      tone: "error",
      text: humaniseError(error)
    };
    url.searchParams.delete("error");
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
  }

  render();

  const [health, session] = await Promise.all([loadHealth(), loadSession()]);
  state.health = health;
  state.session = session;

  if (state.session.status === "ready") {
    state.onboardingComplete = readOnboardingCompletion(state.session.userId);
    if (!state.onboardingComplete && state.activePage === "onboarding") {
      syncDashboardPage("overview", { scroll: false });
    }
    await loadGuildAccess();
  } else {
    state.guildId = health?.guildId ?? null;
    state.dashboardLoading = false;
    render();
  }
}

async function loadHealth(guildId?: string | null) {
  const query = guildId ? `?guildId=${encodeURIComponent(guildId)}` : "";
  try {
    return await apiJson<HealthResponse>(`/api/health${query}`);
  } catch {
    return null;
  }
}

async function loadSession(): Promise<SessionState> {
  try {
    const session = await apiJson<SessionResponse>("/api/auth/me");
    state.onboardingComplete = readOnboardingCompletion(session.userId);
    return {
      status: "ready",
      ...session
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return buildSessionPlaceholder("unauthenticated");
    }

    state.notice = {
      tone: "error",
      text: "Session lookup failed. The dashboard shell is up, but auth could not be verified."
    };
    return buildSessionPlaceholder("unauthenticated");
  }
}

async function loadGuildAccess() {
  state.dashboardLoading = true;
  render();

  try {
    const guildResponse = await apiJson<GuildsResponse>("/api/bot/guilds");
    state.availableGuilds = guildResponse.guilds;

    const url = new URL(window.location.href);
    const requestedGuildId = url.searchParams.get("guild");
    const storedGuildId = readStoredGuildId();
    const defaultGuildId =
      state.session.status === "ready" ? state.session.defaultGuildId ?? null : null;
    const nextGuildId = resolveGuildSelection({
      requestedGuildId,
      storedGuildId,
      defaultGuildId,
      guilds: guildResponse.guilds
    });

    state.guildId = nextGuildId;
    syncGuildSelection(nextGuildId);
    state.health = await loadHealth(nextGuildId);

    if (nextGuildId) {
      await refreshDashboard();
      return;
    }

    state.threat = null;
    state.modules = [];
    state.incidents = [];
    state.incidentCursor = null;
    state.restores = [];
    state.autoMod = null;
    state.autoModDraft = null;
    state.rules = null;
    state.ruleDraft = null;
    state.ruleParseStatus = { state: "idle", message: "" };
    state.dashboardLoading = false;
    state.notice = guildResponse.guilds.length === 0
      ? {
          tone: "info",
          text: "Your Discord account is signed in, but Aegis could not find any shared guilds where you have moderator access."
        }
      : state.notice;
    render();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      state.session = buildSessionPlaceholder("unauthenticated");
      state.notice = {
        tone: "error",
        text: "Your dashboard session expired. Sign in again to continue."
      };
    } else {
      state.notice = {
        tone: "error",
        text: explainActionError("Guild discovery failed", error)
      };
    }
    state.dashboardLoading = false;
    render();
  }
}

async function changeGuild(nextGuildId: string | null) {
  state.guildId = nextGuildId;
  state.selectedIncidentId = null;
  state.threat = null;
  state.modules = [];
  state.incidents = [];
  state.incidentCursor = null;
  state.restores = [];
  state.autoMod = null;
  state.autoModDraft = null;
  state.rules = null;
  state.ruleDraft = null;
  state.ruleParseStatus = { state: "idle", message: "" };
  syncGuildSelection(nextGuildId);
  state.health = await loadHealth(nextGuildId);

  if (!nextGuildId) {
    state.dashboardLoading = false;
    render();
    return;
  }

  await refreshDashboard();
}

function readStoredGuildId() {
  try {
    return window.localStorage.getItem("aegis:selectedGuildId");
  } catch {
    return null;
  }
}

function syncGuildSelection(guildId: string | null) {
  const url = new URL(window.location.href);
  if (guildId) {
    url.searchParams.set("guild", guildId);
  } else {
    url.searchParams.delete("guild");
  }
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);

  try {
    if (guildId) {
      window.localStorage.setItem("aegis:selectedGuildId", guildId);
    } else {
      window.localStorage.removeItem("aegis:selectedGuildId");
    }
  } catch {
    // ignore localStorage availability issues
  }
}

function readStoredDashboardPage() {
  try {
    return window.localStorage.getItem("aegis:dashboardPage");
  } catch {
    return null;
  }
}

function syncDashboardPage(page: DashboardPage, options: { scroll?: boolean } = {}) {
  state.activeView = page;
  state.activePage = page;
  const url = new URL(window.location.href);
  url.hash = page;
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

  try {
    window.localStorage.setItem("aegis:dashboardPage", page);
  } catch {
    // ignore localStorage availability issues
  }

  if (options.scroll !== false) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

function resolveDashboardPage(
  requestedPage: string | null | undefined,
  storedPage: string | null
): DashboardPage {
  const aliases: Record<string, DashboardPage> = {
    operations: "defense",
    policies: "automod"
  };
  const candidates = [requestedPage, storedPage];
  for (const candidate of candidates) {
    const normalised = candidate ? aliases[candidate] ?? candidate : candidate;
    if (
      normalised === "onboarding" ||
      normalised === "overview" ||
      normalised === "defense" ||
      normalised === "incidents" ||
      normalised === "restores" ||
      normalised === "automod" ||
      normalised === "rules"
    ) {
      return normalised;
    }
  }

  return "overview";
}

function readOnboardingCompletion(userId: string) {
  try {
    return window.localStorage.getItem(`aegis:onboardingSeen:${userId}`) === "true";
  } catch {
    return false;
  }
}

function markOnboardingComplete(userId: string) {
  try {
    window.localStorage.setItem(`aegis:onboardingSeen:${userId}`, "true");
  } catch {
    // ignore localStorage availability issues
  }
}

async function refreshDashboard() {
  if (!state.guildId) {
    state.dashboardLoading = false;
    render();
    return;
  }

  state.dashboardLoading = true;
  render();

  try {
    const [threat, modules, incidents, restores, autoMod, rules] = await Promise.all([
      apiJson<ThreatResponse>(`/api/bot/guilds/${state.guildId}/threat-level`),
      apiJson<ModulesResponse>(`/api/bot/guilds/${state.guildId}/modules`),
      apiJson<IncidentsResponse>(`/api/bot/guilds/${state.guildId}/incidents?limit=8`),
      apiJson<RestoreResponse>(`/api/bot/guilds/${state.guildId}/restores?limit=6`),
      apiJson<AutoModPolicyResponse>(`/api/bot/guilds/${state.guildId}/automod-policy`),
      apiJson<RulesListResponse>(`/api/bot/guilds/${state.guildId}/rules`)
    ]);

    state.threat = threat;
    state.modules = modules.modules;
    state.incidents = incidents.items;
    if (state.selectedIncidentId && !incidents.items.some((incident) => incident.id === state.selectedIncidentId)) {
      state.selectedIncidentId = null;
    }
    state.incidentCursor = incidents.pageInfo.nextCursor;
    state.restores = restores.restores;
    state.autoMod = autoMod;
    state.autoModDraft = cloneAutoModPolicy(autoMod.policy);
    state.rules = rules;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      state.session = buildSessionPlaceholder("unauthenticated");
      state.notice = {
        tone: "error",
        text: "Your dashboard session expired. Sign in again to continue."
      };
    } else if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
      state.notice = {
        tone: "error",
        text: "The selected guild is no longer available to this session. Pick another shared guild."
      };
      state.availableGuilds = state.availableGuilds.filter((guild) => guild.id !== state.guildId);
      state.guildId = resolveGuildSelection({
        requestedGuildId: null,
        storedGuildId: readStoredGuildId(),
        defaultGuildId:
          state.session.status === "ready" ? state.session.defaultGuildId ?? null : null,
        guilds: state.availableGuilds
      });
      syncGuildSelection(state.guildId);
    } else {
      state.notice = {
        tone: "error",
        text: `Dashboard data failed to load: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  } finally {
    state.dashboardLoading = false;
    render();
  }
}

function render() {
  root.innerHTML = `
    <main class="shell ${state.session.status === "ready" ? "shell-authenticated" : "shell-public"}">
      ${
        state.session.status === "ready"
          ? renderAuthenticatedShell()
          : renderPublicLanding()
      }
    </main>
  `;

  bindEvents();
}

function bindEvents() {
  root.querySelector<HTMLElement>("[data-action='refresh-all']")?.addEventListener("click", () => {
    if (state.session.status === "ready" && state.availableGuilds.length > 0) {
      void refreshDashboard();
    } else if (state.session.status === "ready") {
      void loadGuildAccess();
    }
  });

  root.querySelector<HTMLElement>("[data-action='logout']")?.addEventListener("click", () => {
    void logout();
  });

  root.querySelectorAll<HTMLElement>("[data-dashboard-page]").forEach((button) => {
    button.addEventListener("click", () => {
      const nextPage = button.dataset.dashboardPage;
      if (
        nextPage === "onboarding" ||
        nextPage === "overview" ||
        nextPage === "defense" ||
        nextPage === "incidents" ||
        nextPage === "restores" ||
        nextPage === "automod" ||
        nextPage === "rules"
      ) {
        state.selectedIncidentId = null;
        syncDashboardPage(nextPage);
        render();
      }
    });
  });

  root.querySelector<HTMLElement>("[data-action='complete-onboarding']")?.addEventListener("click", () => {
    if (state.session.status !== "ready") {
      return;
    }

    state.onboardingComplete = true;
    markOnboardingComplete(state.session.userId);
    syncDashboardPage(state.guildId ? "overview" : "onboarding");
    state.notice = {
      tone: "success",
      text: "Onboarding complete. Your workspace is ready."
    };
    render();
  });

  const guildSelector = root.querySelector("#guild-selector");
  if (guildSelector instanceof HTMLSelectElement) {
    guildSelector.addEventListener("change", () => {
      const nextGuildId = guildSelector.value || null;
      void changeGuild(nextGuildId);
    });
  }

  root.querySelector<HTMLTextAreaElement>("#manual-threat-reason")?.addEventListener("input", (event) => {
    state.threatReasonDraft = (event.currentTarget as HTMLTextAreaElement).value;
  });

  root.querySelectorAll<HTMLElement>("[data-threat-level]").forEach((button) => {
    button.addEventListener("click", () => {
      const level = button.dataset.threatLevel as ThreatLevel | undefined;
      if (!level) {
        return;
      }
      void overrideThreatLevel(level);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-module-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const moduleId = button.dataset.moduleId;
      const nextEnabled = button.dataset.nextEnabled === "true";
      if (!moduleId) {
        return;
      }
      void toggleModule(moduleId, nextEnabled);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-restore-id][data-restore-action]").forEach((button) => {
    button.addEventListener("click", () => {
      const restoreId = button.dataset.restoreId;
      const action = button.dataset.restoreAction;
      if (!restoreId || (action !== "approve" && action !== "reject")) {
        return;
      }
      void handleRestoreAction(restoreId, action);
    });
  });

  root.querySelector<HTMLElement>("[data-action='load-more-incidents']")?.addEventListener("click", () => {
    void loadMoreIncidents();
  });

  root.querySelectorAll<HTMLElement>("[data-open-incident-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const incidentId = button.dataset.openIncidentId;
      if (!incidentId) {
        return;
      }

      state.selectedIncidentId = incidentId;
      render();
    });
    button.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      const incidentId = button.dataset.openIncidentId;
      if (!incidentId) {
        return;
      }

      state.selectedIncidentId = incidentId;
      render();
    });
  });

  root.querySelector<HTMLElement>(".incident-drawer-backdrop")?.addEventListener("click", (event) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    state.selectedIncidentId = null;
    render();
  });

  root.querySelectorAll<HTMLElement>("[data-action='close-incident-drawer']").forEach((button) => {
    button.addEventListener("click", () => {
      state.selectedIncidentId = null;
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-automod-preset]").forEach((button) => {
    button.addEventListener("click", () => {
      const preset = button.dataset.automodPreset;
      if (preset === "starter" || preset === "strict") {
        updateAutoModDraft((draft) => applyAutoModPreset(draft, preset));
      }
    });
  });

  root.querySelectorAll<HTMLElement>("[data-automod-toggle]").forEach((button) => {
    button.addEventListener("click", () => {
      const ruleId = button.dataset.automodRule as AutoModRuleId | undefined;
      const nextEnabled = button.dataset.automodToggle === "true";
      if (!ruleId) {
        return;
      }
      updateAutoModDraft((draft) => {
        const next = cloneAutoModPolicy(draft);
        if (ruleId === "profileNames") {
          next.rules.profileNames.enabled = nextEnabled;
        } else {
          next.rules[ruleId].enabled = nextEnabled;
        }
        return normaliseAutoModPolicy(next);
      });
    });
  });

  root.querySelector<HTMLInputElement>("#automod-mention-limit")?.addEventListener("input", (event) => {
    const value = Number((event.currentTarget as HTMLInputElement).value);
    updateAutoModDraft((draft) => {
      const next = cloneAutoModPolicy(draft);
      next.rules.mentionSpam.mentionLimit = Number.isFinite(value) ? value : draft.mentionLimit;
      return normaliseAutoModPolicy(next);
    });
  });

  root.querySelectorAll<HTMLTextAreaElement>("[data-automod-list]").forEach((textarea) => {
    textarea.addEventListener("input", () => {
      const ruleId = textarea.dataset.automodRule as AutoModRuleId | undefined;
      const field = textarea.dataset.automodField as AutoModListField | undefined;
      if (!ruleId || !field) {
        return;
      }
      updateAutoModDraft((draft) => {
        const next = cloneAutoModPolicy(draft);
        const parsed = parseListInput(textarea.value);
        if (ruleId === "profileNames") {
          next.rules.profileNames[field] = parsed;
        } else if (ruleId === "securityPhrases" || ruleId === "verificationLures") {
          next.rules[ruleId][field] = parsed;
        } else {
          return draft;
        }
        return normaliseAutoModPolicy(next);
      });
    });
  });

  root.querySelectorAll<HTMLElement>("[data-automod-role]").forEach((chip) => {
    chip.addEventListener("click", () => {
      const ruleId = chip.dataset.automodRule as AutoModRuleId | undefined;
      const roleId = chip.dataset.automodRole;
      if (!ruleId || !roleId) {
        return;
      }
      updateAutoModDraft((draft) => {
        const next = cloneAutoModPolicy(draft);
        if (ruleId === "profileNames") {
          next.rules.profileNames.exemptRoleIds = toggleSelection(
            next.rules.profileNames.exemptRoleIds,
            roleId
          );
        } else {
          next.rules[ruleId].exemptRoleIds = toggleSelection(
            next.rules[ruleId].exemptRoleIds,
            roleId
          );
        }
        return normaliseAutoModPolicy(next);
      });
    });
  });

  root.querySelectorAll<HTMLElement>("[data-automod-channel]").forEach((chip) => {
    chip.addEventListener("click", () => {
      const ruleId = chip.dataset.automodRule as AutoModRuleId | undefined;
      const channelId = chip.dataset.automodChannel;
      if (
        !ruleId ||
        !channelId ||
        ruleId === "profileNames"
      ) {
        return;
      }
      updateAutoModDraft((draft) => {
        const next = cloneAutoModPolicy(draft);
        next.rules[ruleId].exemptChannelIds = toggleSelection(
          next.rules[ruleId].exemptChannelIds,
          channelId
        );
        return normaliseAutoModPolicy(next);
      });
    });
  });

  root.querySelector<HTMLElement>("[data-action='save-automod']")?.addEventListener("click", () => {
    void saveAutoModPolicy();
  });

  root.querySelector<HTMLElement>("[data-action='reset-automod']")?.addEventListener("click", () => {
    if (state.autoMod) {
      state.autoModDraft = cloneAutoModPolicy(state.autoMod.policy);
      state.notice = {
        tone: "info",
        text: "AutoMod draft reset to the last saved policy."
      };
      render();
    }
  });

  root
    .querySelector<HTMLElement>("[data-action='remove-automod-managed']")
    ?.addEventListener("click", () => {
      void removeManagedAutoModRules();
    });

  root.querySelector<HTMLElement>("[data-action='rule-new']")?.addEventListener("click", () => {
    state.ruleDraft = createBlankRuleDraft();
    state.ruleParseStatus = { state: "idle", message: "" };
    render();
  });

  root.querySelector<HTMLElement>("[data-action='rule-cancel']")?.addEventListener("click", () => {
    state.ruleDraft = null;
    state.ruleParseStatus = { state: "idle", message: "" };
    render();
  });

  root.querySelector<HTMLElement>("[data-action='rule-save']")?.addEventListener("click", () => {
    void saveRuleDraft();
  });

  root.querySelector<HTMLElement>("[data-action='rule-validate']")?.addEventListener("click", () => {
    void validateRuleConditionRemote();
  });

  root.querySelectorAll<HTMLElement>("[data-action='rule-edit']").forEach((button) => {
    button.addEventListener("click", () => {
      const ruleId = button.dataset.ruleId;
      if (!ruleId || !state.rules) return;
      const target = state.rules.rules.find((rule) => rule.id === ruleId);
      if (!target) return;
      state.ruleDraft = ruleDraftFromDefinition(target);
      state.ruleParseStatus = { state: "idle", message: "" };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='rule-delete']").forEach((button) => {
    button.addEventListener("click", () => {
      const ruleId = button.dataset.ruleId;
      if (!ruleId) return;
      if (!window.confirm(`Delete rule "${ruleId}"? This cannot be undone.`)) return;
      void deleteRule(ruleId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='rule-toggle']").forEach((button) => {
    button.addEventListener("click", () => {
      const ruleId = button.dataset.ruleId;
      const nextEnabled = button.dataset.ruleNextEnabled === "true";
      if (!ruleId) return;
      void toggleRuleEnabled(ruleId, nextEnabled);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='rule-set-enabled']").forEach((button) => {
    button.addEventListener("click", () => {
      if (!state.ruleDraft) return;
      state.ruleDraft = { ...state.ruleDraft, enabled: button.dataset.value === "true" };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='rule-set-action-kind']").forEach((button) => {
    button.addEventListener("click", () => {
      if (!state.ruleDraft) return;
      const next = button.dataset.value as RuleActionKind | undefined;
      if (next !== "incident" && next !== "threatEscalation") return;
      state.ruleDraft = { ...state.ruleDraft, actionKind: next };
      render();
    });
  });

  bindRuleFieldInputs();
}

function bindRuleFieldInputs() {
  if (!state.ruleDraft) return;

  // Updating draft fields without re-rendering keeps focus inside the field.
  // We refresh state silently and only call render() on structural changes
  // (action kind, event type, etc.).
  const setField = <K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) => {
    if (!state.ruleDraft) return;
    state.ruleDraft = { ...state.ruleDraft, [key]: value };
  };

  root.querySelector<HTMLInputElement>("#rule-name")?.addEventListener("input", (event) => {
    setField("name", (event.currentTarget as HTMLInputElement).value);
  });

  const eventSelect = root.querySelector("#rule-event");
  if (eventSelect instanceof HTMLSelectElement) {
    eventSelect.addEventListener("change", () => {
      const next = eventSelect.value as RuleEventName;
      setField("event", next);
      state.ruleParseStatus = { state: "idle", message: "" };
      render();
    });
  }

  root.querySelector<HTMLTextAreaElement>("#rule-when")?.addEventListener("input", (event) => {
    setField("when", (event.currentTarget as HTMLTextAreaElement).value);
    if (state.ruleParseStatus.state !== "idle") {
      state.ruleParseStatus = { state: "idle", message: "" };
      const status = root.querySelector<HTMLElement>("[data-parse-status]");
      if (status) {
        status.className = "rule-parse-status status-idle";
        status.innerHTML = "Click <em>Validate</em> to dry-run the condition before saving.";
      }
    }
  });

  root.querySelector<HTMLInputElement>("#rule-cooldown")?.addEventListener("input", (event) => {
    const raw = Number((event.currentTarget as HTMLInputElement).value);
    setField("cooldownSeconds", Number.isFinite(raw) ? raw : 0);
  });

  const severitySelect = root.querySelector("#rule-severity");
  if (severitySelect instanceof HTMLSelectElement) {
    severitySelect.addEventListener("change", () => {
      setField("severity", severitySelect.value as RuleSeverity);
    });
  }

  root.querySelector<HTMLInputElement>("#rule-summary")?.addEventListener("input", (event) => {
    setField("summaryTemplate", (event.currentTarget as HTMLInputElement).value);
  });

  root.querySelector<HTMLTextAreaElement>("#rule-details")?.addEventListener("input", (event) => {
    setField("detailTemplatesText", (event.currentTarget as HTMLTextAreaElement).value);
  });

  const threatLevelSelect = root.querySelector("#rule-threat-level");
  if (threatLevelSelect instanceof HTMLSelectElement) {
    threatLevelSelect.addEventListener("change", () => {
      setField("threatLevel", threatLevelSelect.value as RuleThreatLevel);
    });
  }

  root.querySelector<HTMLInputElement>("#rule-reason")?.addEventListener("input", (event) => {
    setField("reasonTemplate", (event.currentTarget as HTMLInputElement).value);
  });
}

function renderControlPlaneLine() {
  if (!state.health) {
    return "Health probe unavailable";
  }

  if (state.health.bot?.reachable) {
    const latencySuffix =
      state.health.bot.latencyMs !== undefined ? ` - ${state.health.bot.latencyMs} ms` : "";
    return `Connected${latencySuffix}`;
  }

  return "Bot offline";
}

function formatDashboardViewLabel(view: DashboardView) {
  return formatDashboardPageLabel(view);
}

function renderSessionLine() {
  if (state.session.status === "loading") {
    return "Checking session...";
  }

  if (state.session.status === "unauthenticated") {
    return "Sign in to unlock incidents, restore approvals, module controls, and AutoMod editing.";
  }

  return `${escapeHtml(state.session.username)} signed in - session until ${escapeHtml(
    formatAbsoluteTime(new Date(state.session.expiresAt * 1000).toISOString())
  )}`;
}

function renderNotice(tone: NoticeTone, text: string) {
  return `
    <section class="notice ${tone}">
      <span class="notice-pill">${tone.toUpperCase()}</span>
      <p>${escapeHtml(text)}</p>
    </section>
  `;
}

function formatDashboardPageLabel(page: DashboardPage) {
  switch (page) {
    case "onboarding":
      return "Onboarding";
    case "defense":
      return "Threat & Modules";
    case "incidents":
      return "Incidents";
    case "restores":
      return "Restores";
    case "automod":
      return "AutoMod";
    case "rules":
      return "Rule Engine";
    case "overview":
    default:
      return "Overview";
  }
}

function renderPublicLanding() {
  return `
    <section class="hero hero-shell">
      <div class="hero-copy">
        <span class="eyebrow">Aegis Control Plane</span>
        <h1>Dark-glass command center for Discord defense.</h1>
        <p class="lead">
          Threat posture, restores, incidents, AutoMod policy, and custom rules now live in a
          calmer app shell with a persistent sidebar instead of one giant admin scroll.
        </p>
        <div class="hero-actions">
          <a class="button primary" href="/api/auth/login">Sign in with Discord</a>
        </div>
      </div>
      <div class="hero-aside">
        <div class="glass-card status-card">
          <span class="card-label">Control plane</span>
          <strong>${renderControlPlaneLine()}</strong>
          <p>${renderSessionLine()}</p>
        </div>
        <div class="glass-card metric-stack compact">
          <div>
            <span class="metric-label">Dashboard mode</span>
            <strong>Pages live</strong>
          </div>
          <div>
            <span class="metric-label">Bot heartbeat</span>
            <strong>${state.health?.bot?.reachable ? "Connected" : "Waiting"}</strong>
          </div>
        </div>
      </div>
    </section>
    ${state.notice ? renderNotice(state.notice.tone, state.notice.text) : ""}
    ${renderSignedOutState()}
  `;
}

function renderAuthenticatedShell() {
  return `
    <section class="workspace-shell">
      ${renderSidebar()}
      <section class="workspace-main">
        ${renderWorkspaceHeader()}
        ${state.notice ? renderNotice(state.notice.tone, state.notice.text) : ""}
        ${!state.guildId ? renderNoGuildState() : renderActiveDashboardPage()}
      </section>
    </section>
    ${renderIncidentDrawer()}
  `;
}

function renderSidebar() {
  return `
    <aside class="glass-card sidebar">
      <div class="sidebar-brand">
        <span class="eyebrow">Aegis</span>
        <strong>Closed beta workspace</strong>
        <p>${escapeHtml(renderSessionLine())}</p>
      </div>

      <div class="sidebar-block sidebar-health">
        <span class="card-label">Control plane</span>
        <strong>${renderControlPlaneLine()}</strong>
        <p>Last report ${escapeHtml(formatRelativeTime(state.health?.reportedAt ?? null))}</p>
      </div>

      <div class="sidebar-block">
        <span class="card-label">Guild scope</span>
        ${
          state.session.status === "ready"
            ? renderGuildSelector()
            : `<strong>${escapeHtml(state.guildId ?? "Not configured")}</strong>`
        }
      </div>

      <nav class="sidebar-nav">
        <div class="sidebar-nav-group">
          <span class="sidebar-nav-label">Workspace</span>
          ${renderSidebarNavButton("overview", "Overview", "Snapshot of posture and activity")}
          ${renderSidebarNavButton("defense", "Threat & Modules", "Live response and defense state")}
          ${renderSidebarNavButton(
            "incidents",
            "Incidents",
            `${state.incidents.length} loaded`,
            state.incidents.length ? String(state.incidents.length) : null
          )}
          ${renderSidebarNavButton(
            "restores",
            "Restores",
            "Dual-control queue",
            String(state.restores.filter((restore) => restore.status !== "restored").length)
          )}
        </div>
        <div class="sidebar-nav-group">
          <span class="sidebar-nav-label">Policies</span>
          ${renderSidebarNavButton(
            "automod",
            "AutoMod",
            "Native rule composer",
            String(state.autoMod?.live.aegisManagedRuleCount ?? 0)
          )}
          ${renderSidebarNavButton(
            "rules",
            "Rule Engine",
            "Custom event logic",
            String(state.rules?.rules.length ?? 0)
          )}
        </div>
        <div class="sidebar-nav-group">
          <span class="sidebar-nav-label">Support</span>
          ${renderSidebarNavButton(
            "onboarding",
            state.onboardingComplete ? "Guide" : "Onboarding",
            state.onboardingComplete ? "Revisit setup steps" : "First-run setup walkthrough"
          )}
        </div>
      </nav>

      <div class="sidebar-actions">
        <button class="button primary" data-action="refresh-all">Refresh all</button>
        <button class="button ghost" data-action="logout">Log out</button>
      </div>
    </aside>
  `;
}

function renderSidebarNavButton(
  page: DashboardPage,
  label: string,
  description: string,
  badge: string | null = null
) {
  const disabled = !state.guildId && page !== "onboarding";
  return `
    <button
      type="button"
      class="sidebar-link ${state.activePage === page ? "active" : ""}"
      data-dashboard-page="${page}"
      ${disabled ? "disabled" : ""}
    >
      <span class="sidebar-link-copy">
        <strong>${escapeHtml(label)}</strong>
        <small>${escapeHtml(description)}</small>
      </span>
      ${badge !== null ? `<span class="sidebar-link-badge">${escapeHtml(badge)}</span>` : ""}
    </button>
  `;
}

function renderWorkspaceHeader() {
  const meta = getPageMeta(state.activePage);
  return `
    <section class="glass-card workspace-header">
      <div class="workspace-header-copy">
        <span class="eyebrow">${escapeHtml(meta.eyebrow)}</span>
        <h1>${escapeHtml(meta.title)}</h1>
        <p>${escapeHtml(meta.description)}</p>
      </div>
      <div class="workspace-header-stats">
        ${renderWorkspaceStat("Guild", state.guildId ? "Selected" : "Waiting", state.guildId ?? "Choose a guild")}
        ${renderWorkspaceStat("Last report", formatAbsoluteTime(state.health?.reportedAt ?? null), "Dashboard heartbeat")}
        ${renderWorkspaceStat("Page", formatDashboardPageLabel(state.activePage), meta.shortNote)}
      </div>
    </section>
  `;
}

function renderWorkspaceStat(label: string, value: string, caption: string) {
  return `
    <article class="workspace-stat">
      <span class="metric-label">${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <p>${escapeHtml(caption)}</p>
    </article>
  `;
}

function getPageMeta(page: DashboardPage) {
  switch (page) {
    case "onboarding":
      return {
        eyebrow: "First run",
        title: "Welcome to the Aegis beta.",
        description:
          "Get the guild pointed at the right logs, know where the controls live, and leave this page only once your first-run checklist is covered.",
        shortNote: "Setup guide"
      };
    case "defense":
      return {
        eyebrow: "Defense",
        title: "Threat posture and module controls.",
        description:
          "Operate the guild live from one place: current threat level, cooldown timing, and module availability without mixing incidents and policies into the same scroll.",
        shortNote: "Live controls"
      };
    case "incidents":
      return {
        eyebrow: "Signals",
        title: "Incident timeline and detections.",
        description:
          "Review the latest detections, load more history, and keep the signal feed isolated from editing controls.",
        shortNote: "Signal feed"
      };
    case "restores":
      return {
        eyebrow: "Recovery",
        title: "Restore approvals and recovery flow.",
        description:
          "Handle dual-control restore tickets separately from daily operations so approvals stay deliberate and easy to scan.",
        shortNote: "Approval queue"
      };
    case "automod":
      return {
        eyebrow: "Native policy",
        title: "Discord AutoMod policy composer.",
        description:
          "Tune the managed native-rule surface without dragging incidents and restores into the same workspace.",
        shortNote: "Native rules"
      };
    case "rules":
      return {
        eyebrow: "Custom logic",
        title: "Rule engine and event-based automation.",
        description:
          "Build and adjust Aegis rule logic in its own workspace so the editor gets the room it needs.",
        shortNote: "Custom automations"
      };
    case "overview":
    default:
      return {
        eyebrow: "Overview",
        title: "Operational overview at a glance.",
        description:
          "Start here for the health of the guild, what is armed, and where to dive next without facing a single endless page.",
        shortNote: "Snapshot"
      };
  }
}

function renderViewSwitcher() {
  return `
    <section class="glass-card view-switcher">
      <div class="view-switcher-copy">
        <span class="card-label">Workspace</span>
        <strong>${escapeHtml(formatDashboardViewLabel(state.activeView))}</strong>
        <p>Swap between overview, operations, and policy tuning without dragging through one endless page.</p>
      </div>
      <div class="segmented-control segmented-control-wide">
        ${DASHBOARD_VIEW_ORDER.map(
          (view) => `
            <button
              type="button"
              class="button ${state.activeView === view ? "active" : "ghost"}"
              data-dashboard-view="${view}"
            >
              ${escapeHtml(formatDashboardViewLabel(view))}
            </button>
          `
        ).join("")}
      </div>
    </section>
  `;
}

function renderActiveDashboardPage() {
  switch (state.activePage) {
    case "onboarding":
      return renderOnboardingPage();
    case "defense":
      return renderDefensePage();
    case "incidents":
      return renderIncidentsPage();
    case "restores":
      return renderRestoresPage();
    case "automod":
      return renderAutoModPage();
    case "rules":
      return renderRulesPage();
    case "overview":
    default:
      return renderOverviewPage();
  }
}

function renderOverviewPage() {
  const enabledModules = state.modules.filter((module) => module.enabled).length;
  const openRestores = state.restores.filter((restore) => restore.status !== "restored").length;
  return `
    ${!state.onboardingComplete ? renderOnboardingSpotlight() : ""}
    <section class="summary-grid compact">
      ${renderThreatSummaryCard()}
      ${renderIncidentSummaryCard()}
      ${renderCoverageSummaryCard(enabledModules)}
      ${renderRestoreSummaryCard(openRestores)}
    </section>
    <section class="dashboard-grid overview-grid">
      ${renderThreatPanel()}
      ${renderOverviewDigestPanel()}
      ${renderOverviewPolicyPanel()}
    </section>
  `;
}

function renderThreatSummaryCard() {
  const level = state.threat?.threatLevel ?? "baseline";
  const descriptor =
    level === "critical"
      ? "Immediate moderator attention required."
      : level === "elevated"
        ? "Raised signal detected across recent activity."
        : "System nominal across the last signal window.";
  return renderMetricCard({
    label: "Threat posture",
    value: formatThreatLabel(level),
    caption: state.threat?.reason ?? descriptor,
    tone: level,
    badge: level === "baseline" ? "Healthy" : level === "elevated" ? "Watch" : "Critical",
    trendSvg: renderSparkline(buildThreatTrendPoints(), level),
    featured: true
  });
}

function renderIncidentSummaryCard() {
  const recentCritical = state.incidents.filter((incident) => incident.severity === "critical").length;
  const activeCount = state.incidents.length;
  const badge = recentCritical > 0 ? `${recentCritical} critical` : activeCount > 0 ? "Attention" : "Quiet";
  const tone = recentCritical > 0 ? "critical" : activeCount > 0 ? "elevated" : "neutral";
  return renderMetricCard({
    label: "Incident pressure",
    value: activeCount === 0 ? "No active signals" : `${activeCount} Active signals`,
    caption:
      activeCount === 0
        ? "No active incident load in the current feed."
        : "Recent detections are ready to open inline from the feed.",
    tone,
    badge,
    trendSvg: renderSparkline(buildIncidentTrendPoints(), tone === "neutral" ? "baseline" : tone)
  });
}

function renderCoverageSummaryCard(enabledModules: number) {
  const totalModules = state.modules.length;
  const ratio = totalModules === 0 ? 0 : enabledModules / totalModules;
  const tone: ThreatLevel | "neutral" =
    ratio === 1 ? "baseline" : ratio >= 0.75 ? "elevated" : totalModules === 0 ? "neutral" : "critical";
  return renderMetricCard({
    label: "Protection coverage",
    value: `${enabledModules} Active / ${totalModules || 0} Total`,
    caption:
      totalModules === 0
        ? "Module state is still loading."
        : ratio === 1
          ? "Every protection module is armed."
          : "Some modules are not currently armed in this guild.",
    tone,
    badge: totalModules === 0 ? "Loading" : ratio === 1 ? "Full" : `${Math.round(ratio * 100)}%`,
    progress: ratio
  });
}

function renderRestoreSummaryCard(openRestores: number) {
  const tone: ThreatLevel | "neutral" = openRestores === 0 ? "baseline" : openRestores > 2 ? "critical" : "elevated";
  return renderMetricCard({
    label: "Recovery queue",
    value: openRestores === 0 ? "0 Open Tickets" : `${openRestores} Approval Tickets`,
    caption:
      openRestores === 0
        ? "No active recovery tickets. System is fully stable."
        : "Restore approvals are waiting for moderator review.",
    tone,
    badge: openRestores === 0 ? "Stable" : "Action"
  });
}

function renderMetricCard(options: {
  label: string;
  value: string;
  caption: string;
  tone: ThreatLevel | "neutral";
  badge: string;
  trendSvg?: string;
  progress?: number;
  featured?: boolean;
}) {
  return `
    <article class="glass-card summary-card ${options.tone === "neutral" ? "neutral" : options.tone} ${
      options.featured ? "featured" : ""
    }">
      <div class="summary-card-topline">
        <span class="card-label">${escapeHtml(options.label)}</span>
        <span class="status-badge ${options.tone === "neutral" ? "baseline" : options.tone}">${escapeHtml(options.badge)}</span>
      </div>
      <strong>${escapeHtml(options.value)}</strong>
      <p>${escapeHtml(options.caption)}</p>
      ${
        typeof options.progress === "number"
          ? `<div class="summary-progress" aria-hidden="true">
               <span style="width:${Math.max(0, Math.min(100, Math.round(options.progress * 100)))}%"></span>
             </div>`
          : ""
      }
      ${options.trendSvg ?? ""}
    </article>
  `;
}

function buildIncidentTrendPoints(bucketCount = 8) {
  const now = Date.now();
  const lookbackMs = 24 * 60 * 60 * 1000;
  const startMs = now - lookbackMs;
  const bucketMs = lookbackMs / bucketCount;
  const buckets = new Array<number>(bucketCount).fill(0);

  for (const incident of state.incidents) {
    const timestamp = Date.parse(incident.createdAt);
    if (Number.isNaN(timestamp) || timestamp < startMs || timestamp > now) {
      continue;
    }

    const bucket = Math.min(bucketCount - 1, Math.floor((timestamp - startMs) / bucketMs));
    buckets[bucket] = (buckets[bucket] ?? 0) + severityWeight(incident.severity);
  }

  return buckets;
}

function buildThreatTrendPoints(bucketCount = 8) {
  const points = buildIncidentTrendPoints(bucketCount).map((value) => Math.min(4, Math.max(1, value)));
  const currentLevel = state.threat?.threatLevel ?? "baseline";
  const currentValue = currentLevel === "critical" ? 4 : currentLevel === "elevated" ? 3 : 1;
  points[points.length - 1] = Math.max(points[points.length - 1] ?? 1, currentValue);
  return points;
}

function severityWeight(severity: IncidentRecord["severity"]) {
  switch (severity) {
    case "critical":
      return 4;
    case "high":
      return 3;
    case "medium":
      return 2;
    case "low":
    default:
      return 1;
  }
}

function renderSparkline(points: number[], tone: ThreatLevel) {
  const safePoints = points.length === 0 ? [0, 0, 0, 0] : points;
  const max = Math.max(...safePoints, 1);
  const step = safePoints.length > 1 ? 100 / (safePoints.length - 1) : 100;
  const polyline = safePoints
    .map((value, index) => {
      const x = index * step;
      const y = 36 - (value / max) * 28;
      return `${x},${Number(y.toFixed(2))}`;
    })
    .join(" ");

  return `
    <div class="sparkline sparkline-${tone}" aria-hidden="true">
      <svg viewBox="0 0 100 36" preserveAspectRatio="none">
        <polyline points="${polyline}" />
      </svg>
      <span>Last 24h</span>
    </div>
  `;
}

function renderOnboardingSpotlight() {
  return `
    <section class="glass-card onboarding-spotlight">
      <div class="onboarding-spotlight-copy">
        <span class="eyebrow">First run</span>
        <strong>Start with the real dashboard, not a dead-end intro page.</strong>
        <p>
          The sidebar is live, the guild is selected, and you can work normally right away. If you
          want the setup guide, open onboarding as its own page. Otherwise finish it from here and
          keep moving.
        </p>
      </div>
      <div class="onboarding-spotlight-actions">
        <button class="button primary" data-action="complete-onboarding">Finish onboarding</button>
        <button class="button ghost" data-dashboard-page="onboarding">Open guide</button>
      </div>
    </section>
  `;
}

function renderDefensePage() {
  return `
    <section class="dashboard-grid focus-grid">
      ${renderThreatPanel()}
      ${renderModulePanel()}
    </section>
  `;
}

function renderIncidentsPage() {
  return `
    <section class="dashboard-grid single-panel-grid">
      ${renderIncidentPanel()}
    </section>
  `;
}

function renderRestoresPage() {
  return `
    <section class="dashboard-grid single-panel-grid">
      ${renderRestorePanel()}
    </section>
  `;
}

function renderAutoModPage() {
  return `
    <section class="dashboard-grid single-panel-grid">
      ${renderAutoModPanel()}
    </section>
  `;
}

function renderRulesPage() {
  return `
    <section class="dashboard-grid single-panel-grid">
      ${renderRulePanel()}
    </section>
  `;
}

function renderOnboardingPage() {
  const modulesEnabled = state.modules.filter((module) => module.enabled).length;
  return `
    <section class="dashboard-grid onboarding-grid">
      <section class="glass-card panel onboarding-panel">
        <div class="panel-head">
          <div>
            <span class="card-label">First-run checklist</span>
            <h2>Get the guild ready before you hand this to moderators.</h2>
          </div>
          <span class="muted-pill">${state.onboardingComplete ? "Complete" : "In progress"}</span>
        </div>
        <div class="onboarding-steps">
          ${renderOnboardingStep(
            "01",
            "Select the guild",
            state.guildId
              ? `Aegis is currently focused on ${state.guildId}.`
              : "Pick a guild from the sidebar selector so the rest of the dashboard has a target."
          )}
          ${renderOnboardingStep(
            "02",
            "Check the command surface",
            "Use /aegis and /ping inside Discord so moderators can confirm slash commands and bot permissions are healthy."
          )}
          ${renderOnboardingStep(
            "03",
            "Set mod-log and trust controls",
            "Point the guild at a mod-log channel, confirm staff roles, and make sure restore approvals land in the right hands."
          )}
          ${renderOnboardingStep(
            "04",
            "Review active policies",
            `${modulesEnabled}/${state.modules.length || 0} modules are enabled, ${state.autoMod?.live.aegisManagedRuleCount ?? 0} managed AutoMod rules are live, and ${state.rules?.rules.length ?? 0} custom rules are stored.`
          )}
        </div>
        <div class="button-row">
          <button class="button primary" data-action="complete-onboarding">Enter the dashboard</button>
          <button class="button ghost" data-dashboard-page="overview">Skip to overview</button>
        </div>
      </section>
      <section class="glass-card panel onboarding-panel">
        <div class="panel-head">
          <div>
            <span class="card-label">Quick map</span>
            <h2>What each page is for</h2>
          </div>
        </div>
        <div class="onboarding-map">
          <article class="subtle-card">
            <strong>Overview</strong>
            <p>Snapshot of threat, activity, and policy posture.</p>
          </article>
          <article class="subtle-card">
            <strong>Threat & Modules</strong>
            <p>Threat overrides, lockdown timing, and feature toggles.</p>
          </article>
          <article class="subtle-card">
            <strong>Incidents</strong>
            <p>All recent detections without policy editing in the way.</p>
          </article>
          <article class="subtle-card">
            <strong>Restores</strong>
            <p>Dedicated queue for dual-control approval work.</p>
          </article>
          <article class="subtle-card">
            <strong>AutoMod</strong>
            <p>Managed native Discord AutoMod policy tuning.</p>
          </article>
          <article class="subtle-card">
            <strong>Rule Engine</strong>
            <p>Custom Aegis event rules and conditional logic.</p>
          </article>
        </div>
      </section>
    </section>
  `;
}

function renderOnboardingStep(index: string, title: string, description: string) {
  return `
    <article class="onboarding-step">
      <span class="onboarding-step-index">${escapeHtml(index)}</span>
      <div>
        <strong>${escapeHtml(title)}</strong>
        <p>${escapeHtml(description)}</p>
      </div>
    </article>
  `;
}

function renderSummaryCard(
  label: string,
  value: string,
  caption: string,
  tone: ThreatLevel | "neutral"
) {
  return `
    <article class="glass-card summary-card ${tone === "neutral" ? "neutral" : tone}">
      <span class="card-label">${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <p>${escapeHtml(caption)}</p>
    </article>
  `;
}

function renderOverviewDigestPanel() {
  const incidents = state.incidents.slice(0, 3);

  return `
    <section class="glass-card panel digest-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Incident digest</span>
          <h2>Recent signal at a glance</h2>
        </div>
        <button type="button" class="button ghost compact" data-dashboard-page="incidents">Open incidents</button>
      </div>
      ${
        incidents.length === 0
          ? `<div class="empty-state compact"><p>${escapeHtml(
              state.dashboardLoading ? "Loading incident digest..." : "No incidents recorded yet."
            )}</p></div>`
          : `<div class="incident-list">
              ${incidents
                .map(
                  (incident) => `
                    <article class="incident-row ${incident.severity}">
                      <div class="incident-topline">
                        <span class="severity-chip ${incident.severity}">${escapeHtml(
                          incident.severity.toUpperCase()
                        )}</span>
                        <strong>${escapeHtml(incident.kind)}</strong>
                        <time>${escapeHtml(formatRelativeTime(incident.createdAt))}</time>
                      </div>
                      <p class="incident-summary">${escapeHtml(incident.summary)}</p>
                    </article>
                  `
                )
                .join("")}
            </div>`
      }
    </section>
  `;
}

function renderOverviewPolicyPanel() {
  const managedRules = state.autoMod?.live.liveRules ?? [];
  const topRules = managedRules.slice(0, 3);

  return `
    <section class="glass-card panel digest-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Policy posture</span>
          <h2>AutoMod and rules snapshot</h2>
        </div>
        <button type="button" class="button ghost compact" data-dashboard-page="automod">Open AutoMod</button>
      </div>
      <div class="policy-digest">
        <article class="subtle-card">
          <span class="metric-label">Preset</span>
          <strong>${escapeHtml(state.autoMod?.policy.preset ?? "Unknown")}</strong>
          <p>${state.rules?.rules.length ?? 0} custom rules saved.</p>
        </article>
        <article class="subtle-card">
          <span class="metric-label">Managed rules</span>
          <strong>${managedRules.length}</strong>
          <p>${escapeHtml(state.autoMod?.live.ruleNames.slice(0, 2).join(" · ") || "No live AutoMod rules yet.")}</p>
        </article>
      </div>
      ${
        topRules.length === 0
          ? `<div class="empty-state compact"><p>No live AutoMod rules to summarise yet.</p></div>`
          : `<div class="live-rule-grid">
              ${topRules
                .map(
                  (rule) => `
                    <article class="live-rule-summary">
                      <strong>${escapeHtml(rule.name)}</strong>
                      <p>${rule.keywordCount} keywords · ${rule.regexPatternCount} regex · ${
                        rule.mentionLimit ?? 0
                      } mention cap</p>
                    </article>
                  `
                )
                .join("")}
            </div>`
      }
    </section>
  `;
}

function renderGuildSelector() {
  if (state.availableGuilds.length === 0) {
    return `<strong>No accessible guilds</strong>`;
  }

  return `
    <label class="select-wrap">
      <select id="guild-selector">
        ${state.availableGuilds
          .map(
            (guild) => `
              <option value="${escapeHtml(guild.id)}" ${guild.id === state.guildId ? "selected" : ""}>
                ${escapeHtml(guild.name)}
              </option>
            `
          )
          .join("")}
      </select>
    </label>
  `;
}

function renderSignedOutState() {
  return `
    <section class="signed-out card-wide">
      <div class="glass-card empty-state">
        <span class="card-label">Dashboard locked</span>
        <h2>Sign in to open the command surface.</h2>
        <p>
          Health probes stay public, but incidents, restore actions, module toggles, threat overrides,
          and AutoMod policy edits require a Discord session that the bot can re-resolve against the guild.
        </p>
        <a class="button primary" href="/api/auth/login">Continue with Discord</a>
      </div>
    </section>
  `;
}

function renderNoGuildState() {
  return `
    <section class="signed-out card-wide">
      <div class="glass-card empty-state">
        <span class="card-label">Guild access</span>
        <h2>No shared moderator guilds found.</h2>
        <p>
          Your session is valid, but Aegis could not find any guild where this account currently has moderator access
          through the bot. Invite the bot, add the account to the guild, or promote it into a trusted moderator role.
        </p>
        <button class="button primary" data-action="refresh-all">Check again</button>
      </div>
    </section>
  `;
}

function renderThreatPanel() {
  const threat = state.threat;
  const currentLevel = threat?.threatLevel ?? "baseline";
  return `
    <section class="glass-card panel threat-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Threat level</span>
          <h2>${escapeHtml(formatThreatLabel(currentLevel))}</h2>
        </div>
        <span class="threat-chip ${currentLevel}">${escapeHtml(currentLevel.toUpperCase())}</span>
      </div>
      ${renderSparkline(buildThreatTrendPoints(), currentLevel)}
      <div class="stat-grid">
        <article>
          <span>Cooldown</span>
          <strong>${escapeHtml(formatCountdown(threat?.cooldowns.deescalationMsRemaining ?? 0))}</strong>
        </article>
        <article>
          <span>Lockdown mode</span>
          <strong>${escapeHtml((threat?.lockdownMode ?? "monitor").toUpperCase())}</strong>
        </article>
        <article>
          <span>Last signal</span>
          <strong>${escapeHtml(formatRelativeTime(threat?.lastSignalAt ?? null))}</strong>
        </article>
        <article>
          <span>Source</span>
          <strong>${escapeHtml(threat?.source ?? "No source")}</strong>
        </article>
      </div>
      <div class="subtle-copy">
        <strong>Reason</strong>
        <p>${escapeHtml(threat?.reason ?? "No active threat reason recorded.")}</p>
      </div>
      <label class="input-stack">
        <span>Manual override reason</span>
        <textarea id="manual-threat-reason" placeholder="Explain why you are stepping the guild up or down.">${escapeHtml(
          state.threatReasonDraft
        )}</textarea>
      </label>
      <div class="button-row">
        ${(["baseline", "elevated", "critical"] as ThreatLevel[])
          .map(
            (level) => `
              <button
                class="button ${level === currentLevel ? "active" : "ghost"}"
                data-threat-level="${level}"
                ${state.busyKey === `threat:${level}` ? "disabled" : ""}
              >
                ${escapeHtml(formatThreatLabel(level))}
              </button>
            `
          )
          .join("")}
      </div>
    </section>
  `;
}

function renderModulePanel() {
  const enabledCount = state.modules.filter((module) => module.enabled).length;
  const ratio = state.modules.length === 0 ? 0 : enabledCount / state.modules.length;
  return `
    <section class="glass-card panel module-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Module controls</span>
          <h2>Live protection toggles</h2>
        </div>
        <span class="muted-pill">${enabledCount} Active / ${state.modules.length} Total</span>
      </div>
      <div class="coverage-meter">
        <div class="summary-progress" aria-hidden="true">
          <span style="width:${Math.round(ratio * 100)}%"></span>
        </div>
        <p>${state.modules.length === 0 ? "Module state is still loading." : ratio === 1 ? "All modules are armed." : "Some protections need attention."}</p>
      </div>
      <div class="module-list">
        ${
          state.modules.length === 0
            ? `<p class="empty-copy">Module state is still loading.</p>`
            : state.modules
                .map(
                  (module) => `
                    <article class="module-row">
                      <div>
                        <div class="module-title-line">
                          <strong>${escapeHtml(module.label)}</strong>
                          <span class="tier-chip ${module.tier}">${escapeHtml(module.tier.toUpperCase())}</span>
                        </div>
                        <p>${escapeHtml(module.description)}</p>
                      </div>
                      <button
                        class="button ${module.enabled ? "secondary" : "ghost"}"
                        data-module-id="${escapeHtml(module.id)}"
                        data-next-enabled="${String(!module.enabled)}"
                        ${!module.available || state.busyKey === `module:${module.id}` ? "disabled" : ""}
                      >
                        ${module.enabled ? "Disable" : "Enable"}
                      </button>
                    </article>
                  `
                )
                .join("")
        }
      </div>
    </section>
  `;
}

function renderAutoModPanel() {
  const response = state.autoMod;
  const draft = state.autoModDraft;
  const dirty =
    response && draft
      ? JSON.stringify(normaliseAutoModPolicy(draft)) !== JSON.stringify(response.policy)
      : false;

  return `
    <section class="glass-card panel automod-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Discord AutoMod</span>
          <h2>Visual policy composer</h2>
        </div>
        <div class="button-row compact">
          <button
            class="button primary"
            data-action="save-automod"
            ${!draft || state.busyKey === "automod:save" ? "disabled" : ""}
          >
            Save policy
          </button>
          <button
            class="button ghost"
            data-action="reset-automod"
            ${!draft || !dirty ? "disabled" : ""}
          >
            Reset draft
          </button>
          <button
            class="button ghost"
            data-action="remove-automod-managed"
            ${!response || state.busyKey === "automod:remove" ? "disabled" : ""}
          >
            Remove managed rules
          </button>
        </div>
      </div>
      ${
        !response || !draft
          ? `<div class="empty-state compact"><p>${state.dashboardLoading ? "Loading AutoMod policy..." : "AutoMod policy data is not available yet."}</p></div>`
          : `
            <div class="stat-grid automod-summary-grid">
              <article>
                <span>Guild rules</span>
                <strong>${response.live.totalRuleCount}</strong>
              </article>
              <article>
                <span>Aegis managed</span>
                <strong>${response.live.aegisManagedRuleCount}</strong>
              </article>
              <article>
                <span>Alert channel</span>
                <strong>${escapeHtml(response.live.alertChannelId ? `#${response.live.alertChannelId}` : "Not configured")}</strong>
              </article>
              <article>
                <span>Badge target</span>
                <strong>${response.badgeTarget} app-wide rules</strong>
              </article>
            </div>
            <div class="subtle-copy">
              <strong>Policy note</strong>
              <p>
                This editor composes real Discord AutoMod rules. The profile badge is still app-wide:
                Discord awards <code>Uses AutoMod</code> once the application reaches ${response.badgeTarget} rules across all servers, not in one guild.
              </p>
            </div>
            <div class="preset-row">
              <div class="segmented-control">
                ${(["starter", "strict"] as const)
                  .map(
                    (preset) => `
                      <button
                        class="button ${draft.preset === preset ? "active" : "ghost"}"
                        data-automod-preset="${preset}"
                      >
                        ${preset === "strict" ? "Strict" : "Starter"}
                      </button>
                    `
                  )
                  .join("")}
              </div>
              <label class="input-inline">
                <span>Mention limit</span>
                <input
                  id="automod-mention-limit"
                  type="number"
                  min="3"
                  max="50"
                  value="${draft.mentionLimit}"
                />
              </label>
              <span class="muted-pill">${dirty ? "Unsaved draft" : "Saved state"}</span>
            </div>
            <div class="automod-rule-grid">
              ${AUTO_MOD_RULE_ORDER.map((ruleId) => renderAutoModRuleCard(ruleId, draft, response)).join("")}
            </div>
          `
      }
    </section>
  `;
}

function renderAutoModRuleCard(
  ruleId: AutoModRuleId,
  draft: AutoModPolicy,
  response: AutoModPolicyResponse
) {
  const meta = AUTO_MOD_RULE_META[ruleId];
  const liveRule = response.live.liveRules.find((candidate) => candidate.key === ruleId) ?? null;
  const roles = response.catalog.roles;
  const channels = response.catalog.channels;

  if (ruleId === "profileNames") {
    const rule = draft.rules.profileNames;
    return `
      <article class="automod-rule-card">
        <div class="module-title-line">
          <strong>${escapeHtml(meta.title)}</strong>
          <span class="muted-pill">${rule.enabled ? "Enabled" : "Disabled"}</span>
        </div>
        <p>${escapeHtml(meta.description)}</p>
        ${renderLiveRuleSummary(ruleId, liveRule)}
        <div class="button-row compact">
          <button
            class="button ${rule.enabled ? "secondary" : "ghost"}"
            data-automod-rule="${ruleId}"
            data-automod-toggle="${String(!rule.enabled)}"
          >
            ${rule.enabled ? "Disable" : "Enable"}
          </button>
        </div>
        ${renderListEditor(ruleId, "keywords", "Blocked terms", rule.keywords)}
        ${renderListEditor(ruleId, "regexPatterns", "Regex patterns", rule.regexPatterns)}
        ${renderListEditor(ruleId, "allowList", "Allow-list phrases", rule.allowList)}
        ${renderRoleChipGroup(ruleId, roles, rule.exemptRoleIds)}
      </article>
    `;
  }

  const rule = draft.rules[ruleId];
  return `
    <article class="automod-rule-card">
      <div class="module-title-line">
        <strong>${escapeHtml(meta.title)}</strong>
        <span class="muted-pill">${rule.enabled ? "Enabled" : "Disabled"}</span>
      </div>
      <p>${escapeHtml(meta.description)}</p>
      ${renderLiveRuleSummary(ruleId, liveRule)}
      <div class="button-row compact">
        <button
          class="button ${rule.enabled ? "secondary" : "ghost"}"
          data-automod-rule="${ruleId}"
          data-automod-toggle="${String(!rule.enabled)}"
        >
          ${rule.enabled ? "Disable" : "Enable"}
        </button>
      </div>
      ${renderKeywordEditorGroup(ruleId, rule)}
      ${renderRoleChipGroup(ruleId, roles, rule.exemptRoleIds)}
      ${meta.supportsChannels ? renderChannelChipGroup(ruleId, channels, rule.exemptChannelIds) : ""}
    </article>
  `;
}

function renderLiveRuleSummary(ruleId: AutoModRuleId, liveRule: AutoModPolicyResponse["live"]["liveRules"][number] | null) {
  if (!liveRule) {
    return `
      <div class="live-rule-summary">
        <span class="card-label">Live status</span>
        <p>${ruleId === "profileNames" ? "No live member-profile rule is armed yet." : "No live managed rule is armed right now."}</p>
      </div>
    `;
  }

  const detailBits = [
    liveRule.keywordCount > 0 ? `${liveRule.keywordCount} keywords` : null,
    liveRule.regexPatternCount > 0 ? `${liveRule.regexPatternCount} regex` : null,
    liveRule.allowListCount > 0 ? `${liveRule.allowListCount} allow-list` : null,
    liveRule.mentionLimit !== null ? `mention cap ${liveRule.mentionLimit}` : null,
    liveRule.exemptRoleIds.length > 0 ? `${liveRule.exemptRoleIds.length} role exemptions` : null,
    liveRule.exemptChannelIds.length > 0
      ? `${liveRule.exemptChannelIds.length} channel exemptions`
      : null
  ].filter(Boolean);

  return `
    <div class="live-rule-summary">
      <span class="card-label">Live status</span>
      <p>
        ${liveRule.enabled ? "Armed in Discord AutoMod." : "Present but disabled."}
        ${detailBits.length > 0 ? ` ${escapeHtml(detailBits.join(" - "))}.` : ""}
      </p>
    </div>
  `;
}

function renderListEditor(
  ruleId: AutoModRuleId,
  field: AutoModListField,
  label: string,
  values: string[]
) {
  return `
    <label class="input-stack">
      <span>${escapeHtml(label)}</span>
      <textarea
        data-automod-list="true"
        data-automod-rule="${ruleId}"
        data-automod-field="${field}"
        placeholder="One entry per line"
      >${escapeHtml(serialiseListInput(values))}</textarea>
    </label>
  `;
}

function renderKeywordEditorGroup(
  ruleId: Exclude<AutoModRuleId, "profileNames">,
  rule: AutoModPolicy["rules"][Exclude<AutoModRuleId, "profileNames">]
) {
  if (ruleId === "securityPhrases") {
    const keywordRule = rule as AutoModPolicy["rules"]["securityPhrases"];
    return `
      ${renderListEditor(ruleId, "keywords", "Custom keywords", keywordRule.keywords)}
      ${renderListEditor(ruleId, "regexPatterns", "Regex patterns", keywordRule.regexPatterns)}
      ${renderListEditor(ruleId, "allowList", "Allow-list phrases", keywordRule.allowList)}
    `;
  }

  if (ruleId === "verificationLures") {
    const keywordRule = rule as AutoModPolicy["rules"]["verificationLures"];
    return `
      ${renderListEditor(ruleId, "keywords", "Custom keywords", keywordRule.keywords)}
      ${renderListEditor(ruleId, "regexPatterns", "Regex patterns", keywordRule.regexPatterns)}
      ${renderListEditor(ruleId, "allowList", "Allow-list phrases", keywordRule.allowList)}
    `;
  }

  return "";
}

function renderRoleChipGroup(
  ruleId: AutoModRuleId,
  roles: AutoModPolicyResponse["catalog"]["roles"],
  selectedRoleIds: string[]
) {
  return `
    <div class="chip-group-stack">
      <span class="card-label">Exempt roles</span>
      <div class="chip-group">
        ${
          roles.length === 0
            ? `<span class="chip empty">No guild roles available.</span>`
            : roles
                .map(
                  (role) => `
                    <button
                      class="chip ${selectedRoleIds.includes(role.id) ? "selected" : ""}"
                      type="button"
                      data-automod-rule="${ruleId}"
                      data-automod-role="${role.id}"
                    >
                      @${escapeHtml(role.name)}
                    </button>
                  `
                )
                .join("")
        }
      </div>
    </div>
  `;
}

function renderChannelChipGroup(
  ruleId: Exclude<AutoModRuleId, "profileNames">,
  channels: AutoModPolicyResponse["catalog"]["channels"],
  selectedChannelIds: string[]
) {
  return `
    <div class="chip-group-stack">
      <span class="card-label">Exempt channels</span>
      <div class="chip-group">
        ${
          channels.length === 0
            ? `<span class="chip empty">No guild channels available.</span>`
            : channels
                .map(
                  (channel) => `
                    <button
                      class="chip ${selectedChannelIds.includes(channel.id) ? "selected" : ""}"
                      type="button"
                      data-automod-rule="${ruleId}"
                      data-automod-channel="${channel.id}"
                    >
                      #${escapeHtml(channel.name)}
                    </button>
                  `
                )
                .join("")
        }
      </div>
    </div>
  `;
}

function renderRulePanel() {
  const data = state.rules;
  const draft = state.ruleDraft;
  const rules = data ? sortRulesByRecency(data.rules) : [];
  const limitReached = data ? data.rules.length >= data.catalog.limits.maxRulesPerGuild : false;

  return `
    <section class="glass-card panel rule-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Rule Engine</span>
          <h2>Custom DSL rules</h2>
        </div>
        <div class="button-row compact">
          <button
            class="button primary"
            data-action="rule-new"
            ${!data || draft !== null || limitReached || state.busyKey?.startsWith("rule:") ? "disabled" : ""}
          >
            ${limitReached ? "Limit reached" : "New rule"}
          </button>
        </div>
      </div>
      ${
        !data
          ? `<div class="empty-state compact"><p>${state.dashboardLoading ? "Loading rules..." : "Rules data is not available yet."}</p></div>`
          : `
            <div class="subtle-copy">
              <strong>How rules work</strong>
              <p>
                Each rule binds a guild event to a boolean DSL condition. When the condition matches, Aegis emits
                an incident or escalates threat. Use <code>{{fieldName}}</code> templates to interpolate event data
                into messages. Cooldowns prevent flood loops on noisy events.
              </p>
            </div>
            ${draft ? renderRuleEditor(draft, data) : ""}
            <div class="rule-list">
              ${
                rules.length === 0
                  ? `<div class="empty-state compact"><p>No custom rules yet. Click <strong>New rule</strong> to add one.</p></div>`
                  : rules.map((rule) => renderRuleCard(rule)).join("")
              }
            </div>
          `
      }
    </section>
  `;
}

function renderRuleCard(rule: RuleDefinition) {
  const busy = state.busyKey === `rule:${rule.id}`;
  const editing = state.ruleDraft?.id === rule.id;
  const actions = rule.actions.map((action) => describeRuleAction(action)).join(", ");

  return `
    <article class="rule-card ${rule.enabled ? "enabled" : "disabled"} ${editing ? "editing" : ""}">
      <header class="rule-card-head">
        <div>
          <strong>${escapeHtml(rule.name)}</strong>
          <span class="rule-meta">
            <span class="rule-badge ${rule.enabled ? "on" : "off"}">${rule.enabled ? "On" : "Off"}</span>
            <span>${escapeHtml(RULE_EVENT_LABELS[rule.event])}</span>
            <span>${escapeHtml(actions)}</span>
            ${rule.cooldownSeconds > 0 ? `<span>${rule.cooldownSeconds}s cooldown</span>` : ""}
          </span>
        </div>
        <div class="button-row compact">
          <button
            class="button ghost compact"
            data-action="rule-toggle"
            data-rule-id="${escapeHtml(rule.id)}"
            data-rule-next-enabled="${rule.enabled ? "false" : "true"}"
            ${busy ? "disabled" : ""}
          >
            ${rule.enabled ? "Disable" : "Enable"}
          </button>
          <button
            class="button ghost compact"
            data-action="rule-edit"
            data-rule-id="${escapeHtml(rule.id)}"
            ${state.ruleDraft !== null || busy ? "disabled" : ""}
          >
            Edit
          </button>
          <button
            class="button danger compact"
            data-action="rule-delete"
            data-rule-id="${escapeHtml(rule.id)}"
            ${busy ? "disabled" : ""}
          >
            Delete
          </button>
        </div>
      </header>
      <pre class="rule-condition"><code>${escapeHtml(rule.when)}</code></pre>
      ${rule.actions.map((action) => renderRuleActionSummary(action)).join("")}
    </article>
  `;
}

function renderRuleActionSummary(action: RuleDefinition["actions"][number]) {
  if (action.type === "incident") {
    const detailLines = action.detailTemplates.length > 0
      ? `<ul class="rule-detail-list">${action.detailTemplates
          .map((line) => `<li>${escapeHtml(line)}</li>`)
          .join("")}</ul>`
      : "";
    return `
      <div class="rule-action-summary">
        <span class="card-label">Incident · ${escapeHtml(RULE_SEVERITY_LABELS[action.severity])}</span>
        <p>${escapeHtml(action.summaryTemplate)}</p>
        ${detailLines}
      </div>
    `;
  }

  return `
    <div class="rule-action-summary">
      <span class="card-label">Threat · ${escapeHtml(RULE_THREAT_LABELS[action.level])}</span>
      <p>${escapeHtml(action.reasonTemplate)}</p>
    </div>
  `;
}

function renderRuleEditor(draft: RuleDraft, data: RulesListResponse) {
  const fields = data.catalog.fields[draft.event] ?? [];
  const isEditing = draft.id !== null;
  const busy = state.busyKey === "rule:save";
  const parseStatus = state.ruleParseStatus;
  const limits = data.catalog.limits;

  return `
    <div class="rule-editor glass-card-inner">
      <div class="panel-head">
        <div>
          <span class="card-label">${isEditing ? "Edit rule" : "New rule"}</span>
          <h3>${isEditing ? escapeHtml(draft.name || "Editing rule") : "Compose a new rule"}</h3>
        </div>
        <div class="button-row compact">
          <button
            class="button primary"
            data-action="rule-save"
            ${busy ? "disabled" : ""}
          >
            ${isEditing ? "Save changes" : "Create rule"}
          </button>
          <button
            class="button ghost"
            data-action="rule-cancel"
            ${busy ? "disabled" : ""}
          >
            Cancel
          </button>
        </div>
      </div>

      <div class="rule-form">
        <label class="rule-field">
          <span>Name</span>
          <input
            type="text"
            id="rule-name"
            maxlength="${limits.maxNameLength}"
            value="${escapeHtml(draft.name)}"
            placeholder="Catch deleted-invite joins"
          />
        </label>

        <label class="rule-field">
          <span>Event</span>
          <select id="rule-event">
            ${data.catalog.events
              .map(
                (event) => `
                  <option value="${event}" ${event === draft.event ? "selected" : ""}>
                    ${escapeHtml(RULE_EVENT_LABELS[event])}
                  </option>
                `
              )
              .join("")}
          </select>
        </label>

        <label class="rule-field rule-field-wide">
          <span>Condition</span>
          <textarea
            id="rule-when"
            rows="2"
            maxlength="${limits.maxWhenLength}"
            placeholder='attributionKind == "deleted-invite"'
          >${escapeHtml(draft.when)}</textarea>
          <div class="rule-parse-status status-${parseStatus.state}" data-parse-status>
            ${
              parseStatus.state === "idle"
                ? "Click <em>Validate</em> to dry-run the condition before saving."
                : parseStatus.state === "checking"
                  ? "Validating..."
                  : escapeHtml(parseStatus.message)
            }
          </div>
          <div class="button-row compact">
            <button class="button ghost compact" data-action="rule-validate" ${busy ? "disabled" : ""}>
              Validate
            </button>
          </div>
        </label>

        <div class="rule-field">
          <span>Available fields for <code>${escapeHtml(draft.event)}</code></span>
          <ul class="rule-field-catalog">
            ${fields
              .map(
                (entry) => `
                  <li>
                    <code>${escapeHtml(entry.name)}</code>
                    <span class="rule-field-type">${escapeHtml(entry.type)}</span>
                    ${entry.note ? `<span class="rule-field-note">${escapeHtml(entry.note)}</span>` : ""}
                  </li>
                `
              )
              .join("")}
          </ul>
        </div>

        <label class="rule-field">
          <span>Cooldown (seconds)</span>
          <input
            type="number"
            id="rule-cooldown"
            min="0"
            max="${limits.maxCooldownSeconds}"
            step="1"
            value="${draft.cooldownSeconds}"
          />
        </label>

        <label class="rule-field">
          <span>Status</span>
          <div class="segmented-control">
            <button
              type="button"
              class="button ${draft.enabled ? "active" : "ghost"} compact"
              data-action="rule-set-enabled"
              data-value="true"
            >
              Enabled
            </button>
            <button
              type="button"
              class="button ${!draft.enabled ? "active" : "ghost"} compact"
              data-action="rule-set-enabled"
              data-value="false"
            >
              Disabled
            </button>
          </div>
        </label>

        <label class="rule-field">
          <span>Action type</span>
          <div class="segmented-control">
            <button
              type="button"
              class="button ${draft.actionKind === "incident" ? "active" : "ghost"} compact"
              data-action="rule-set-action-kind"
              data-value="incident"
            >
              Emit incident
            </button>
            <button
              type="button"
              class="button ${draft.actionKind === "threatEscalation" ? "active" : "ghost"} compact"
              data-action="rule-set-action-kind"
              data-value="threatEscalation"
            >
              Escalate threat
            </button>
          </div>
        </label>

        ${draft.actionKind === "incident" ? renderIncidentActionForm(draft, limits) : renderThreatActionForm(draft, limits)}
      </div>
    </div>
  `;
}

function renderIncidentActionForm(draft: RuleDraft, limits: RulesListResponse["catalog"]["limits"]) {
  return `
    <label class="rule-field">
      <span>Severity</span>
      <select id="rule-severity">
        ${(["low", "medium", "high", "critical"] as RuleSeverity[])
          .map(
            (sev) => `
              <option value="${sev}" ${sev === draft.severity ? "selected" : ""}>
                ${escapeHtml(RULE_SEVERITY_LABELS[sev])}
              </option>
            `
          )
          .join("")}
      </select>
    </label>

    <label class="rule-field rule-field-wide">
      <span>Summary template</span>
      <input
        type="text"
        id="rule-summary"
        maxlength="${limits.maxSummaryLength}"
        value="${escapeHtml(draft.summaryTemplate)}"
        placeholder="Suspicious join captured for {{memberId}}"
      />
    </label>

    <label class="rule-field rule-field-wide">
      <span>Detail templates (one per line, ${limits.maxDetailLines} max)</span>
      <textarea
        id="rule-details"
        rows="4"
        placeholder="Reason: {{reason}}&#10;Trust score: {{trustScore}}"
      >${escapeHtml(draft.detailTemplatesText)}</textarea>
    </label>
  `;
}

function renderThreatActionForm(draft: RuleDraft, limits: RulesListResponse["catalog"]["limits"]) {
  return `
    <label class="rule-field">
      <span>Threat level</span>
      <select id="rule-threat-level">
        ${(["elevated", "critical"] as RuleThreatLevel[])
          .map(
            (level) => `
              <option value="${level}" ${level === draft.threatLevel ? "selected" : ""}>
                ${escapeHtml(RULE_THREAT_LABELS[level])}
              </option>
            `
          )
          .join("")}
      </select>
    </label>

    <label class="rule-field rule-field-wide">
      <span>Reason template</span>
      <input
        type="text"
        id="rule-reason"
        maxlength="${limits.maxReasonLength}"
        value="${escapeHtml(draft.reasonTemplate)}"
        placeholder="Rule {{ruleId}} matched on {{event}}"
      />
    </label>
  `;
}

function renderRestorePanel() {
  const openRestores = state.restores.filter((restore) => restore.status !== "restored").length;
  return `
    <section class="glass-card panel restore-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Restore queue</span>
          <h2>Pending dual-control tickets</h2>
        </div>
        <span class="muted-pill">${openRestores} open</span>
      </div>
      ${
        state.restores.length === 0
          ? `<div class="empty-state compact"><p>No active recovery tickets. System is fully stable.</p></div>`
          : `<div class="restore-list">
              ${state.restores
                .map(
                  (restore) => `
                    <article class="restore-card">
                      <header>
                        <strong>${escapeHtml(restore.id)}</strong>
                        <span class="restore-status ${restore.status}">${escapeHtml(
                          restore.status.replaceAll("_", " ")
                        )}</span>
                      </header>
                      <p>${escapeHtml(restore.reason)}</p>
                      <dl>
                        <div><dt>Actor</dt><dd>${escapeHtml(restore.actorId)}</dd></div>
                        <div><dt>Source</dt><dd>${escapeHtml(restore.source)}</dd></div>
                        <div><dt>Approvals</dt><dd>${restore.approvals.length}/${restore.requiredApprovals}</dd></div>
                        <div><dt>Created</dt><dd>${escapeHtml(formatRelativeTime(restore.createdAt))}</dd></div>
                      </dl>
                      <div class="button-row">
                        <button
                          class="button secondary"
                          data-restore-id="${escapeHtml(restore.id)}"
                          data-restore-action="approve"
                          ${state.busyKey === `restore:${restore.id}` ? "disabled" : ""}
                        >
                          Approve
                        </button>
                        <button
                          class="button ghost"
                          data-restore-id="${escapeHtml(restore.id)}"
                          data-restore-action="reject"
                          ${state.busyKey === `restore:${restore.id}` ? "disabled" : ""}
                        >
                          Reject
                        </button>
                      </div>
                    </article>
                  `
                )
                .join("")}
            </div>`
      }
    </section>
  `;
}

function renderIncidentPanel() {
  if (state.incidents.length === 0) {
    const emptyMessage = state.dashboardLoading ? "Loading incident feed..." : "No incidents recorded yet.";
    return `
      <section class="glass-card panel incident-panel">
        <div class="panel-head">
          <div>
            <span class="card-label">Incident feed</span>
            <h2>Recent detections and operator actions</h2>
          </div>
          <span class="muted-pill">${state.incidents.length} loaded</span>
        </div>
        <div class="empty-state compact">
          <p>${escapeHtml(emptyMessage)}</p>
        </div>
      </section>
    `;
  }

  return `
    <section class="glass-card panel incident-panel">
      <div class="panel-head">
        <div>
          <span class="card-label">Incident feed</span>
          <h2>Recent detections and operator actions</h2>
        </div>
        <span class="muted-pill">${state.incidents.length} loaded</span>
      </div>
      <div class="incident-list">
        ${state.incidents
          .map(
            (incident) => `
              <article
                class="incident-row ${incident.severity} ${state.selectedIncidentId === incident.id ? "selected" : ""}"
                data-open-incident-id="${escapeHtml(incident.id)}"
                role="button"
                tabindex="0"
              >
                <div class="incident-topline">
                  <span class="severity-chip ${incident.severity}">${escapeHtml(
                    incident.severity.toUpperCase()
                  )}</span>
                  <strong>${escapeHtml(incident.kind)}</strong>
                  <time>${escapeHtml(formatRelativeTime(incident.createdAt))}</time>
                </div>
                <p class="incident-summary">${escapeHtml(incident.summary)}</p>
                ${
                  incident.details.length > 0
                    ? `<ul class="detail-list">
                        ${incident.details
                          .slice(0, 3)
                          .map((detail) => `<li>${escapeHtml(detail)}</li>`)
                          .join("")}
                      </ul>`
                    : ""
                }
                <span class="incident-open-hint">Open details</span>
              </article>
            `
          )
          .join("")}
      </div>
      ${
        state.incidentCursor
          ? `<div class="panel-footer">
              <button class="button ghost" data-action="load-more-incidents" ${
                state.busyKey === "incidents:load-more" ? "disabled" : ""
              }>
                Load more
              </button>
            </div>`
          : ""
      }
    </section>
  `;
}

function renderIncidentDrawer() {
  if (!state.selectedIncidentId) {
    return "";
  }

  const incident = state.incidents.find((entry) => entry.id === state.selectedIncidentId);
  if (!incident) {
    return "";
  }

  return `
    <div class="incident-drawer-backdrop" data-action="close-incident-drawer">
      <aside class="glass-card incident-drawer" role="dialog" aria-modal="true" aria-label="Incident details">
        <div class="incident-drawer-head">
          <div>
            <span class="card-label">Incident detail</span>
            <h2>${escapeHtml(incident.kind)}</h2>
          </div>
          <button class="button ghost compact" data-action="close-incident-drawer">Close</button>
        </div>
        <div class="incident-drawer-meta">
          <span class="severity-chip ${incident.severity}">${escapeHtml(incident.severity.toUpperCase())}</span>
          <span>${escapeHtml(formatAbsoluteTime(incident.createdAt))}</span>
          <span>${escapeHtml(incident.actorId ?? "System detected")}</span>
        </div>
        <p class="incident-drawer-summary">${escapeHtml(incident.summary)}</p>
        ${
          incident.details.length > 0
            ? `<ul class="detail-list drawer-list">
                ${incident.details.map((detail) => `<li>${escapeHtml(detail)}</li>`).join("")}
              </ul>`
            : `<p class="incident-drawer-empty">No extra detail lines were attached to this incident.</p>`
        }
      </aside>
    </div>
  `;
}

function updateAutoModDraft(transformer: (draft: AutoModPolicy) => AutoModPolicy) {
  if (!state.autoModDraft) {
    return;
  }

  state.autoModDraft = normaliseAutoModPolicy(transformer(state.autoModDraft));
  render();
}

async function saveAutoModPolicy() {
  if (!state.guildId || !state.autoModDraft) {
    return;
  }

  state.busyKey = "automod:save";
  render();

  try {
    const response = await apiJson<AutoModPolicyResponse>(`/api/bot/guilds/${state.guildId}/automod-policy`, {
      method: "PUT",
      body: JSON.stringify({
        policy: normaliseAutoModPolicy(state.autoModDraft)
      })
    });
    state.autoMod = response;
    state.autoModDraft = cloneAutoModPolicy(response.policy);
    state.notice = {
      tone: "success",
      text: `AutoMod policy saved. ${response.live.aegisManagedRuleCount} Aegis-managed rules are now armed in this guild.`
    };
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("AutoMod policy update failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function removeManagedAutoModRules() {
  if (!state.guildId) {
    return;
  }

  state.busyKey = "automod:remove";
  render();

  try {
    const response = await apiJson<AutoModPolicyResponse>(`/api/bot/guilds/${state.guildId}/automod-policy/remove-managed`, {
      method: "POST"
    });
    state.autoMod = response;
    state.autoModDraft = cloneAutoModPolicy(response.policy);
    state.notice = {
      tone: "success",
      text: `Removed ${response.removal?.removed ?? 0} Aegis-managed AutoMod rules from the guild.`
    };
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Removing managed AutoMod rules failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function saveRuleDraft() {
  if (!state.guildId || !state.ruleDraft || !state.rules) {
    return;
  }

  const validation = validateRuleDraft(state.ruleDraft, state.rules.catalog);
  if (!validation.ok) {
    state.notice = { tone: "error", text: validation.error };
    render();
    return;
  }

  state.busyKey = "rule:save";
  render();

  try {
    const isUpdate = state.ruleDraft.id !== null;
    const url = isUpdate
      ? `/api/bot/guilds/${state.guildId}/rules/${encodeURIComponent(state.ruleDraft.id!)}`
      : `/api/bot/guilds/${state.guildId}/rules`;

    const response = await apiJson<{ rules: RuleDefinition[] }>(url, {
      method: isUpdate ? "PATCH" : "POST",
      body: JSON.stringify(validation.payload)
    });

    if (state.rules) {
      state.rules = { ...state.rules, rules: response.rules };
    }
    state.ruleDraft = null;
    state.ruleParseStatus = { state: "idle", message: "" };
    state.notice = {
      tone: "success",
      text: isUpdate ? "Rule updated." : "Rule created."
    };
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Saving the rule failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function deleteRule(ruleId: string) {
  if (!state.guildId) return;

  state.busyKey = `rule:${ruleId}`;
  render();

  try {
    const response = await apiJson<{ rules: RuleDefinition[] }>(
      `/api/bot/guilds/${state.guildId}/rules/${encodeURIComponent(ruleId)}`,
      { method: "DELETE" }
    );
    if (state.rules) {
      state.rules = { ...state.rules, rules: response.rules };
    }
    state.notice = { tone: "success", text: `Rule ${ruleId} deleted.` };
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Deleting the rule failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function toggleRuleEnabled(ruleId: string, nextEnabled: boolean) {
  if (!state.guildId) return;

  state.busyKey = `rule:${ruleId}`;
  render();

  try {
    const response = await apiJson<{ rules: RuleDefinition[] }>(
      `/api/bot/guilds/${state.guildId}/rules/${encodeURIComponent(ruleId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ enabled: nextEnabled })
      }
    );
    if (state.rules) {
      state.rules = { ...state.rules, rules: response.rules };
    }
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Toggling the rule failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function validateRuleConditionRemote() {
  if (!state.guildId || !state.ruleDraft) return;

  const candidate = state.ruleDraft.when.trim();
  if (!candidate) {
    state.ruleParseStatus = { state: "invalid", message: "Condition is empty." };
    render();
    return;
  }

  state.ruleParseStatus = { state: "checking", message: "Validating..." };
  render();

  try {
    const response = await apiJson<RuleParseResponse>(
      `/api/bot/guilds/${state.guildId}/rules/parse`,
      {
        method: "POST",
        body: JSON.stringify({ when: candidate })
      }
    );
    state.ruleParseStatus = response.ok
      ? { state: "valid", message: "Condition parses cleanly." }
      : { state: "invalid", message: response.error ?? "Condition could not be parsed." };
  } catch (error) {
    state.ruleParseStatus = {
      state: "invalid",
      message: error instanceof Error ? error.message : String(error)
    };
  } finally {
    render();
  }
}

async function overrideThreatLevel(level: ThreatLevel) {
  if (!state.guildId) {
    return;
  }

  const reason = state.threatReasonDraft.trim();
  state.busyKey = `threat:${level}`;
  render();

  try {
    state.threat = await apiJson<ThreatResponse>(`/api/bot/guilds/${state.guildId}/threat-level`, {
      method: "POST",
      body: JSON.stringify({
        threatLevel: level,
        ...(reason ? { reason } : {})
      })
    });
    state.threatReasonDraft = "";
    state.notice = {
      tone: "success",
      text: `Threat level updated to ${formatThreatLabel(level)}.`
    };
    await reloadIncidents();
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Threat override failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function toggleModule(moduleId: string, enabled: boolean) {
  if (!state.guildId) {
    return;
  }

  state.busyKey = `module:${moduleId}`;
  render();

  try {
    const response = await apiJson<{ modules: ModuleState[] }>(
      `/api/bot/guilds/${state.guildId}/modules/${moduleId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ enabled })
      }
    );
    state.modules = response.modules;
    state.notice = {
      tone: "success",
      text: `${moduleId} ${enabled ? "enabled" : "disabled"}.`
    };
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Module update failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function handleRestoreAction(restoreId: string, action: "approve" | "reject") {
  if (!state.guildId) {
    return;
  }

  state.busyKey = `restore:${restoreId}`;
  render();

  try {
    await apiJson(`/api/bot/guilds/${state.guildId}/restores/${restoreId}/${action}`, {
      method: "POST"
    });
    state.notice = {
      tone: "success",
      text: `Restore ticket ${restoreId} ${action === "approve" ? "approved" : "rejected"}.`
    };
    await reloadRestores();
    await reloadIncidents();
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError(`Restore ${action} failed`, error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function loadMoreIncidents() {
  if (!state.guildId || !state.incidentCursor) {
    return;
  }

  state.busyKey = "incidents:load-more";
  render();

  try {
    const response = await apiJson<IncidentsResponse>(
      `/api/bot/guilds/${state.guildId}/incidents?limit=8&cursor=${encodeURIComponent(state.incidentCursor)}`
    );
    state.incidents = [...state.incidents, ...response.items];
    state.incidentCursor = response.pageInfo.nextCursor;
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Failed to load more incidents", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function reloadIncidents() {
  if (!state.guildId) {
    return;
  }

  const response = await apiJson<IncidentsResponse>(`/api/bot/guilds/${state.guildId}/incidents?limit=8`);
  state.incidents = response.items;
  state.incidentCursor = response.pageInfo.nextCursor;
}

async function reloadRestores() {
  if (!state.guildId) {
    return;
  }

  const response = await apiJson<RestoreResponse>(`/api/bot/guilds/${state.guildId}/restores?limit=6`);
  state.restores = response.restores;
}

async function logout() {
  state.busyKey = "logout";
  render();

  try {
    const response = await fetch("/api/auth/logout", { method: "POST" });
    if (!response.ok) {
      throw new ApiError(response.status, "logout_failed", "Logout failed");
    }

    state.session = buildSessionPlaceholder("unauthenticated");
    state.guildId = null;
    state.availableGuilds = [];
    state.threat = null;
    state.modules = [];
    state.incidents = [];
    state.selectedIncidentId = null;
    state.restores = [];
    state.autoMod = null;
    state.autoModDraft = null;
    syncGuildSelection(null);
    state.notice = {
      tone: "success",
      text: "Session closed. Sign in again when you are ready."
    };
  } catch (error) {
    state.notice = {
      tone: "error",
      text: explainActionError("Logout failed", error)
    };
  } finally {
    state.busyKey = null;
    render();
  }
}

async function apiJson<T>(input: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  headers.set("accept", "application/json");

  const response = await fetch(input, {
    ...init,
    headers
  });
  const text = await response.text();
  const data = text ? (JSON.parse(text) as T | { error?: string; detail?: string }) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof data === "object" && data && "error" in data && typeof data.error === "string"
        ? data.error
        : "request_failed",
      typeof data === "object" && data && "detail" in data && typeof data.detail === "string"
        ? data.detail
        : response.statusText || "Request failed"
    );
  }

  return data as T;
}

function explainActionError(prefix: string, error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      state.session = buildSessionPlaceholder("unauthenticated");
      return `${prefix}. Your session expired, so sign in again.`;
    }

    if (error.status === 403) {
      return `${prefix}. Your Discord member permissions do not allow that action.`;
    }

    return `${prefix}. ${humaniseError(error.code)}`;
  }

  return `${prefix}. ${error instanceof Error ? error.message : String(error)}`;
}

function humaniseError(code: string) {
  return code
    .replaceAll(/[_-]+/g, " ")
    .replace(/^\w/, (match) => match.toUpperCase());
}

class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
  }
}

function getRoot() {
  const element = document.getElementById("app");
  if (!element) {
    throw new Error("dashboard root missing");
  }

  return element;
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
